import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { MdCheckBox, MdCheckCircle, MdVerifiedUser } from "react-icons/md";
import { FaBoxOpen, FaChartLine, FaCrown, FaHandshake, FaShieldAlt, FaStore } from "react-icons/fa";
import { GiFamilyTree } from "react-icons/gi";
import { setSelectedRoleOverride } from "../../includes/pageInside/Rolle";
import { persistSelectedRole } from "../../includes/auth";
import { elakayAutoText } from "../../includes/pageInside/dataElakay";
import "./RoleSwitchDropdown.css";

// Display labels only -- the actual allow-list always comes from the
// backend (infoSource.allowedSwitchRoles, computed in
// server_elakay/elakay_services/roleSwitchContext.js from the real
// account). "User" (the implicit base) is a valid backend destination but
// deliberately never offered in this menu -- the spec's own examples only
// ever list Royalty("User Royal")..Family.
const ROLE_LABELS = {
  Royalty: () => elakayAutoText("roleSwitch_role_royalty", "User Royale"),
  Seller: () => elakayAutoText("roleSwitch_role_seller", "Seller"),
  Provider: () => elakayAutoText("roleSwitch_role_provider", "Provider"),
  Associate: () => elakayAutoText("roleSwitch_role_associate", "Associate"),
  Investment: () => elakayAutoText("roleSwitch_role_investment", "Investor"),
  Administrator: () => elakayAutoText("roleSwitch_role_administrator", "Administrator"),
  Family: () => elakayAutoText("roleSwitch_role_family", "Developer"),
};

const ROLE_ICONS = {
  Royalty: FaCrown,
  Seller: FaStore,
  Provider: FaBoxOpen,
  Associate: FaHandshake,
  Investment: FaChartLine,
  Administrator: FaShieldAlt,
  Family: GiFamilyTree,
};

// Switch Role / View As Role (Phase 1). Purely a VIEW overlay: never
// touches userActiveRole, never calls any Become Seller/Provider/Role
// Change Request/cancellation endpoint. Selecting a role here only ever
// narrows the current account's own already-authorized permissions for the
// duration of the switch -- it can never grant anything the account
// doesn't already have.
//
// mode="dropdown" (default -- Header ONLY, per explicit product decision:
// the click-to-open panel/"modal" behavior belongs in the header menu and
// nowhere else): a trigger button (shows "Switch Role" when Default, or
// "Role View: X" when active) opens a panel with the Default option, every
// allowed role, and a "Viewing as X" / "Return to Default" line.
//   - variant="floating": the panel is rendered through a React portal
//     straight into document.body, positioned with `position:fixed`
//     computed from the trigger's own bounding rect. This is deliberate,
//     not decorative -- the Header's nav `<ul>` (.elakay_liste_header ul)
//     has `overflow:hidden` for its own slide-in animation, which silently
//     clips an ordinary CSS `position:absolute` panel nested inside it.
//
// mode="list" (drawer and footer column): no trigger, no
// open/close state, no panel -- just an always-visible checkbox list
// (Default + every allowed role, the current selection checked). The
// surrounding page supplies its own matching section title (e.g.
// ".settings-group-title"/".footer-title"/".menu-section-title") --
// this component renders only the rows.
const RoleSwitchDropdown = ({ infoSource, triggerClassName, triggerStyle, variant = "floating", mode = "dropdown" }) => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const allowed = infoSource?.allowedSwitchRoles || [];
  const selectedRole = infoSource?.selectedRole || null;
  const isActive = !!selectedRole;

  // Nothing to switch to besides one's own real role (or not logged in at
  // all) -- no point rendering a menu with only "Default" in it.
  const switchableRoles = allowed.filter((role) => ROLE_LABELS[role]);

  const isFloating = variant === "floating";
  const isListMode = mode === "list";
  const isCardsMode = mode === "cards";

  const computePanelPos = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
  };

  useEffect(() => {
    if (isListMode || isCardsMode || !open) return undefined;
    if (isFloating) computePanelPos();

    const handleOutside = (e) => {
      const insideTrigger = containerRef.current && containerRef.current.contains(e.target);
      const insidePanel = e.target.closest?.(".role-switch-dropdown__panel");
      if (!insideTrigger && !insidePanel) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === "Escape") setOpen(false); };
    // Closing on scroll/resize (rather than repositioning) keeps this
    // simple and matches how most native dropdowns behave on scroll.
    const handleDismiss = () => setOpen(false);

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    if (isFloating) {
      window.addEventListener("scroll", handleDismiss, true);
      window.addEventListener("resize", handleDismiss);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
      if (isFloating) {
        window.removeEventListener("scroll", handleDismiss, true);
        window.removeEventListener("resize", handleDismiss);
      }
    };
  }, [open, isFloating, isListMode, isCardsMode]);

  if (switchableRoles.length === 0) return null;

  const applySelection = (roleName) => {
    if ((roleName || null) === selectedRole) return;
    infoSource?.setSelectedRole?.(roleName);
    setSelectedRoleOverride(roleName, allowed);
    persistSelectedRole(roleName);
    setOpen(false);
  };

  if (isCardsMode) {
    const renderCard = (role, active, label, Icon) => (
      <button
        key={role ?? "default"}
        type="button"
        className={`role-switch-card${active ? " role-switch-card--active" : ""}`}
        aria-pressed={active}
        aria-label={active
          ? elakayAutoText("roleSwitch_activeRoleLabel", "{value0}, active role", { value0: label })
          : elakayAutoText("roleSwitch_selectRoleLabel", "Switch to {value0} role", { value0: label })}
        disabled={active}
        onClick={() => applySelection(role)}
      >
        <span className="role-switch-card__icon" aria-hidden="true"><Icon /></span>
        <span className="role-switch-card__label">{label}</span>
        <span className={`role-switch-card__status${active ? " role-switch-card__status--active" : ""}`} aria-hidden={!active}>
          {active ? <><MdCheckCircle /> {elakayAutoText("roleSwitch_activeBadge", "Active")}</> : "\u00a0"}
        </span>
      </button>
    );

    return (
      <div className="role-switch-dropdown role-switch-dropdown--cards">
        <div className="role-switch-cards-grid" role="group" aria-label={elakayAutoText("roleSwitch_menuLabel", "Switch Role")}>
          {renderCard(null, !isActive, elakayAutoText("roleSwitch_defaultCard", "Default"), MdVerifiedUser)}
          {switchableRoles.map((role) => renderCard(role, selectedRole === role, ROLE_LABELS[role](), ROLE_ICONS[role]))}
        </div>
      </div>
    );
  }

  // mode="list": plain always-visible checkbox rows, no trigger/panel at
  // all. Single-select underneath (only one role can be active at a time),
  // presented as checkboxes per request -- clicking any row always selects
  // it, the same way a radio group would. The checkbox ICON only appears
  // for the selected row; unselected rows reserve the same space but stay
  // invisible (no empty/unchecked box drawn) per explicit request.
  //
  // Deliberately a <div role="checkbox"> here, NOT a <button>: several
  // pages define global `<context> button { ... }` rules for their own CTA
  // buttons (e.g. Footer.css/elakay_index.css's `.navFooter button`, a
  // pill-shaped uppercase style meant for a "Subscribe"-style action) that
  // would otherwise silently reskin these rows just by matching the tag.
  // A plain div with keyboard support sidesteps that class of collision
  // entirely, in the footer and everywhere else this renders.
  if (isListMode) {
    const renderRow = (role, checked, label) => (
      <div
        key={role ?? "default"}
        role="checkbox"
        tabIndex={0}
        aria-checked={checked}
        className={`role-switch-checkbox-row${checked ? " role-switch-checkbox-row--checked" : ""}`}
        onClick={() => applySelection(role)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); applySelection(role); } }}
      >
        <MdCheckBox className="role-switch-checkbox-icon" aria-hidden="true" />
        <span>{label}</span>
      </div>
    );
    return (
      <div className="role-switch-dropdown role-switch-dropdown--list">
        {renderRow(null, !isActive, elakayAutoText("roleSwitch_defaultOption", "Default / All permissions"))}
        {switchableRoles.map((role) => renderRow(role, selectedRole === role, ROLE_LABELS[role]()))}
      </div>
    );
  }

  const currentLabel = isActive
    ? elakayAutoText("roleSwitch_triggerActive", "Role View: {value0}", { value0: ROLE_LABELS[selectedRole]?.() || selectedRole })
    : elakayAutoText("roleSwitch_menuLabel", "Switch Role");

  const panelContent = (
    <div
      className={`role-switch-dropdown__panel${isFloating ? " role-switch-dropdown__panel--floating" : ""}`}
      style={isFloating && panelPos ? { top: panelPos.top, right: panelPos.right } : undefined}
      role="menu"
    >
      <div className="role-switch-dropdown__panel-title">
        {elakayAutoText("roleSwitch_menuLabel", "Switch Role")}
      </div>

      <button
        type="button"
        role="menuitemradio"
        aria-checked={!isActive}
        className={`role-switch-dropdown__item${!isActive ? " role-switch-dropdown__item--selected" : ""}`}
        onClick={() => applySelection(null)}
      >
        <span className="role-switch-dropdown__check">{!isActive ? "✓" : ""}</span>
        {elakayAutoText("roleSwitch_defaultOption", "Default / All permissions")}
      </button>

      <div className="role-switch-dropdown__divider" />

      {switchableRoles.map((role) => (
        <button
          key={role}
          type="button"
          role="menuitemradio"
          aria-checked={selectedRole === role}
          className={`role-switch-dropdown__item${selectedRole === role ? " role-switch-dropdown__item--selected" : ""}`}
          onClick={() => applySelection(role)}
        >
          <span className="role-switch-dropdown__check">{selectedRole === role ? "✓" : ""}</span>
          {ROLE_LABELS[role]()}
        </button>
      ))}

      <div className="role-switch-dropdown__footer">
        {isActive ? (
          <>
            <span className="role-switch-dropdown__badge role-switch-dropdown__badge--active">
              {elakayAutoText("roleSwitch_viewingAs", "Viewing as {value0}", { value0: ROLE_LABELS[selectedRole]?.() || selectedRole })}
            </span>
            <button
              type="button"
              className="role-switch-dropdown__return-btn"
              onClick={() => applySelection(null)}
            >
              {elakayAutoText("roleSwitch_returnToDefault", "Return to Default")}
            </button>
          </>
        ) : (
          <span className="role-switch-dropdown__badge">
            {elakayAutoText("roleSwitch_defaultBadge", "Switch Role: Default")}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className={`role-switch-dropdown role-switch-dropdown--${variant}`} ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={[
          "role-switch-dropdown__trigger",
          !triggerClassName && "role-switch-dropdown__trigger--fallback",
          isActive && "role-switch-dropdown__trigger--active",
          triggerClassName,
        ].filter(Boolean).join(" ")}
        style={triggerStyle}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {currentLabel}
        <span className="role-switch-dropdown__caret" aria-hidden="true">▾</span>
      </button>

      {open && (isFloating ? ReactDOM.createPortal(panelContent, document.body) : panelContent)}
    </div>
  );
};

export default RoleSwitchDropdown;
